import React, { useCallback, useState, createContext, useContext } from 'react';
type Lang = 'en' | 'es';
interface LanguageContextType {
  lang: Lang;
  toggleLang: () => void;
  t: (key: string) => string;
}
const translations: Record<string, Record<Lang, string>> = {
  // Navbar
  'nav.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'nav.matches': {
    en: 'Matches',
    es: 'Partidos'
  },
  'nav.deals': {
    en: 'Deals',
    es: 'Ofertas'
  },
  'nav.liveScores': {
    en: 'Live Scores',
    es: 'Resultados en Vivo'
  },
  'nav.orderYours': {
    en: 'Order Yours',
    es: 'Compra la Tuya'
  },
  // Hero
  'hero.badge': {
    en: "You're now part of Mexico 2026",
    es: 'Ahora eres parte de México 2026'
  },
  'hero.title1': {
    en: "Don't Just Support Mexico,",
    es: 'No Solo Apoyes a México,'
  },
  'hero.title2': {
    en: 'Live It',
    es: 'Vívelo'
  },
  'hero.subtitle': {
    en: 'Updated in real live time. Access exclusive content, match updates, and travel deals directly from your digital hub as we host the world in 2026.',
    es: 'Actualizado en tiempo real. Accede a contenido exclusivo, actualizaciones de partidos y ofertas de viaje directamente desde tu hub digital mientras somos anfitriones del mundo en 2026.'
  },
  'hero.cta1': {
    en: 'Watch Matches',
    es: 'Ver Partidos'
  },
  'hero.cta2': {
    en: 'Explore Team',
    es: 'Explorar Equipo'
  },
  'hero.scroll': {
    en: 'Scroll to discover',
    es: 'Desliza para descubrir'
  },
  // Team Overview
  'team.label': {
    en: 'Our Legacy',
    es: 'Nuestro Legado'
  },
  'team.title1': {
    en: 'A Nation Forged',
    es: 'Una Nación Forjada'
  },
  'team.title2': {
    en: 'In Passion',
    es: 'En Pasión'
  },
  'team.desc': {
    en: 'The Mexican national team represents the vibrant heart and soul of a country that lives and breathes football. As the only nation to host the World Cup three times (1970, 1986, 2026), El Tri carries the hopes of millions, driven by an unmatched, passionate fanbase.',
    es: 'La selección mexicana representa el corazón vibrante y el alma de un país que vive y respira fútbol. Como la única nación en albergar la Copa del Mundo tres veces (1970, 1986, 2026), El Tri lleva las esperanzas de millones, impulsado por una afición inigualable y apasionada.'
  },
  'team.since': {
    en: 'Since 1923',
    es: 'Desde 1923'
  },
  'team.history': {
    en: 'Explore Our Full History →',
    es: 'Explorar Nuestra Historia Completa →'
  },
  'team.euro': {
    en: '2x WC Host',
    es: '2x Anfitrión Mundial'
  },
  'team.euroDesc': {
    en: 'Hosted two unforgettable World Cups (1970 & 1986)',
    es: 'Organizó dos Mundiales inolvidables (1970 y 1986)'
  },
  'team.nationsLeague': {
    en: 'Gold Cup',
    es: 'Copa Oro'
  },
  'team.nationsLeagueDesc': {
    en: '12-time CONCACAF champions',
    es: '12 veces campeones de CONCACAF'
  },
  'team.euros': {
    en: 'Olympic Gold',
    es: 'Oro Olímpico'
  },
  'team.eurosDesc': {
    en: 'Historic triumph in London 2012',
    es: 'Triunfo histórico en Londres 2012'
  },
  'team.worldCups': {
    en: '17 World Cups',
    es: '17 Mundiales'
  },
  'team.worldCupsDesc': {
    en: 'Most appearances from CONCACAF',
    es: 'Más participaciones de CONCACAF'
  },
  'team.videoLabel': {
    en: 'Sangre Mexicana',
    es: 'Sangre Mexicana'
  },
  'team.videoTitle1': {
    en: 'One Nation, One',
    es: 'Una Nación, Un'
  },
  'team.videoTitle2': {
    en: 'Heartbeat',
    es: 'Latido'
  },
  // Key Players
  'players.label': {
    en: 'The Squad',
    es: 'La Convocatoria'
  },
  'players.title1': {
    en: 'The Stars of',
    es: 'Las Estrellas de'
  },
  'players.title2': {
    en: 'Mexico',
    es: 'México'
  },
  'players.viewSquad': {
    en: 'View Full Squad on ESPN.com',
    es: 'Ver Plantel Completo en ESPN.com'
  },
  'players.forward': {
    en: 'Forward',
    es: 'Delantero'
  },
  'players.midfielder': {
    en: 'Midfielder',
    es: 'Mediocampista'
  },
  'players.defender': {
    en: 'Defender',
    es: 'Defensa'
  },
  'players.goalkeeper': {
    en: 'Goalkeeper',
    es: 'Portero'
  },
  // Match Center
  'matches.label': {
    en: 'World Cup 2026',
    es: 'Mundial 2026'
  },
  'matches.title1': {
    en: 'Match',
    es: 'Centro de'
  },
  'matches.title2': {
    en: 'Center',
    es: 'Partidos'
  },
  'matches.desc': {
    en: 'Follow El Tri in Group A as the host nation. Get live updates, official lineups, and real-time scores from the biggest stage in football.',
    es: 'Sigue al Tri en el Grupo A como nación anfitriona. Obtén actualizaciones en vivo, alineaciones oficiales y resultados en tiempo real del mayor escenario del fútbol.'
  },
  'matches.standings': {
    en: 'Group Standings',
    es: 'Clasificación del Grupo'
  },
  'matches.live': {
    en: 'Live',
    es: 'En Vivo'
  },
  'matches.autoRefresh': {
    en: 'Auto-refreshes every 5 min',
    es: 'Se actualiza cada 5 min'
  },
  'matches.refreshNow': {
    en: 'Refresh Now',
    es: 'Actualizar Ahora'
  },
  'matches.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'matches.viewStandings': {
    en: 'View Live Standings on FlashScore',
    es: 'Ver Clasificación en Vivo en FlashScore'
  },
  'matches.nextMatch': {
    en: 'Next Match',
    es: 'Próximo Partido'
  },
  'matches.espn': {
    en: 'ESPN Schedule',
    es: 'Calendario ESPN'
  },
  'matches.sofascore': {
    en: 'SofaScore',
    es: 'SofaScore'
  },
  'matches.flashscore': {
    en: 'FlashScore Live',
    es: 'FlashScore en Vivo'
  },
  'matches.foxsports': {
    en: 'Fox Sports',
    es: 'Fox Sports'
  },
  // Stats
  'stats.label': {
    en: 'Mexico By The Numbers',
    es: 'México en Números'
  },
  'stats.worldCup': {
    en: 'World Cup Appearances',
    es: 'Participaciones en Mundiales'
  },
  'stats.titles': {
    en: 'Major Titles',
    es: 'Títulos Principales'
  },
  'stats.goals': {
    en: 'International Goals',
    es: 'Goles Internacionales'
  },
  'stats.ronaldo': {
    en: 'Chicharito Goals (Record)',
    es: 'Goles de Chicharito (Récord)'
  },
  // Historic Moments
  'history.label': {
    en: 'The Archives',
    es: 'Los Archivos'
  },
  'history.title1': {
    en: 'Moments That',
    es: 'Momentos Que'
  },
  'history.title2': {
    en: 'Made History',
    es: 'Hicieron Historia'
  },
  // Fan Experience
  'fan.label': {
    en: 'More Than A Game',
    es: 'Más Que Un Juego'
  },
  'fan.title1': {
    en: 'Feel the Passion of',
    es: 'Siente la Pasión de'
  },
  'fan.title2': {
    en: 'Mexico',
    es: 'México'
  },
  'fan.desc': {
    en: 'Join millions of voices singing Cielito Lindo as one. Wear the green, white, and red, feel the history, and be part of the journey to global glory on home soil.',
    es: 'Únete a millones de voces cantando Cielito Lindo como una sola. Viste el verde, blanco y rojo, siente la historia y sé parte del viaje hacia la gloria mundial en casa.'
  },
  // Tickets / Travel Deals
  'deals.label': {
    en: 'Travel to the World Cup',
    es: 'Viaja al Mundial'
  },
  'deals.title1': {
    en: 'Exclusive',
    es: 'Ofertas Exclusivas'
  },
  'deals.title2': {
    en: 'Fan Deals',
    es: 'para Aficionados'
  },
  'deals.desc': {
    en: 'Exclusive discounts for Mexico fans on tickets, hotels, flights & transport to support El Tri across North America.',
    es: 'Descuentos exclusivos para aficionados de México en boletos, hoteles, vuelos y transporte para apoyar al Tri en toda Norteamérica.'
  },
  'deals.unlockVip': {
    en: 'Unlock VIP Discounts',
    es: 'Desbloquea Descuentos VIP'
  },
  'deals.applyCode': {
    en: 'Apply code at checkout on partner sites',
    es: 'Aplica el código en el checkout de sitios asociados'
  },
  'deals.tickets': {
    en: 'Match Tickets',
    es: 'Boletos para Partidos'
  },
  'deals.ticketsDesc': {
    en: 'Up to 15% off group stage packages',
    es: 'Hasta 15% de descuento en paquetes de fase de grupos'
  },
  'deals.findTickets': {
    en: 'Find Tickets',
    es: 'Encontrar Boletos'
  },
  'deals.hotels': {
    en: 'Hotels & Accommodation',
    es: 'Hoteles y Alojamiento'
  },
  'deals.hotelsDesc': {
    en: 'Partner hotel deals near match venues',
    es: 'Ofertas de hoteles asociados cerca de los estadios'
  },
  'deals.bookHotels': {
    en: 'Book Hotels',
    es: 'Reservar Hoteles'
  },
  'deals.flights': {
    en: 'Flights',
    es: 'Vuelos'
  },
  'deals.flightsDesc': {
    en: 'Special fares from Mexico City & Guadalajara to USA/Canada',
    es: 'Tarifas especiales desde CDMX y Guadalajara a EE.UU./Canadá'
  },
  'deals.searchFlights': {
    en: 'Search Flights',
    es: 'Buscar Vuelos'
  },
  'deals.transport': {
    en: 'Local Transport',
    es: 'Transporte Local'
  },
  'deals.transportDesc': {
    en: 'Shuttle services & car rentals at match cities',
    es: 'Servicios de transporte y alquiler de autos en ciudades sede'
  },
  'deals.planTransport': {
    en: 'Plan Transport',
    es: 'Planear Transporte'
  },
  // Fan HQ / Gallery
  'fanHq.label': {
    en: 'Everything You Need for Mexico 2026',
    es: 'Todo lo que Necesitas para México 2026'
  },
  'fanHq.title1': {
    en: 'Fan',
    es: 'Sede del'
  },
  'fanHq.title2': {
    en: 'HQ',
    es: 'Aficionado'
  },
  'fanHq.countdown': {
    en: "Countdown to Mexico's First Match",
    es: 'Cuenta Regresiva para el Primer Partido de México'
  },
  'fanHq.days': {
    en: 'Days',
    es: 'Días'
  },
  'fanHq.hrs': {
    en: 'Hrs',
    es: 'Hrs'
  },
  'fanHq.min': {
    en: 'Min',
    es: 'Min'
  },
  'fanHq.sec': {
    en: 'Sec',
    es: 'Seg'
  },
  'fanHq.capacity': {
    en: 'Capacity',
    es: 'Capacidad'
  },
  'fanHq.chants': {
    en: 'Learn the',
    es: 'Aprende los'
  },
  'fanHq.chantsHighlight': {
    en: 'Chants',
    es: 'Cánticos'
  },
  'fanHq.nationalAnthem': {
    en: 'National Anthem',
    es: 'Himno Nacional'
  },
  'fanHq.classicChant': {
    en: 'Classic Stadium Chant',
    es: 'Cántico Clásico de Estadio'
  },
  'fanHq.crowdFavorite': {
    en: 'Crowd Favorite',
    es: 'Favorito de la Afición'
  },
  'fanHq.victoryChant': {
    en: 'Victory Chant',
    es: 'Cántico de Victoria'
  },
  'fanHq.watchYoutube': {
    en: 'Watch on YouTube',
    es: 'Ver en YouTube'
  },
  'fanHq.phrases': {
    en: 'Essential',
    es: 'Frases'
  },
  'fanHq.phrasesHighlight': {
    en: 'Phrases',
    es: 'Esenciales'
  },
  'fanHq.quickLinks': {
    en: 'Quick',
    es: 'Enlaces'
  },
  'fanHq.quickLinksHighlight': {
    en: 'Links',
    es: 'Rápidos'
  },
  'fanHq.whereToWatch': {
    en: 'Where to Watch',
    es: 'Dónde Ver'
  },
  'fanHq.whereToWatchDesc': {
    en: 'Streaming & TV channels',
    es: 'Canales de TV y streaming'
  },
  'fanHq.fanZones': {
    en: 'Fan Info Channel',
    es: 'Canal de Info para Fans'
  },
  'fanHq.fanZonesDesc': {
    en: 'Official FIFA fan information',
    es: 'Información oficial de FIFA para fans'
  },
  'fanHq.fifaRanking': {
    en: 'FIFA Ranking',
    es: 'Ranking FIFA'
  },
  'fanHq.fifaRankingDesc': {
    en: 'Official world rankings',
    es: 'Clasificación mundial oficial'
  },
  'fanHq.fullSchedule': {
    en: 'Full WC Schedule',
    es: 'Calendario Completo del Mundial'
  },
  'fanHq.fullScheduleDesc': {
    en: 'All 104 matches',
    es: 'Todos los 104 partidos'
  },
  // Footer
  'footer.desc': {
    en: 'An official Qtap by STDI digital product. Your smart NFC T-shirt hub for Mexico 2026 — real-time match data, exclusive content, and travel deals at your fingertips.',
    es: 'Un producto digital oficial de Qtap by STDI. Tu hub de camiseta NFC inteligente para México 2026 — datos de partidos en tiempo real, contenido exclusivo y ofertas de viaje a tu alcance.'
  },
  'footer.resources': {
    en: 'Resources',
    es: 'Recursos'
  },
  'footer.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'footer.firstTeam': {
    en: 'First Team',
    es: 'Primer Equipo'
  },
  'footer.matches': {
    en: 'Matches',
    es: 'Partidos'
  },
  'footer.standings': {
    en: 'Standings',
    es: 'Posiciones'
  },
  'footer.travelDeals': {
    en: 'Travel Deals',
    es: 'Ofertas de Viaje'
  },
  'footer.support': {
    en: 'Support',
    es: 'Soporte'
  },
  'footer.contactUs': {
    en: 'Contact Us',
    es: 'Contáctanos'
  },
  'footer.faq': {
    en: 'FAQ',
    es: 'Preguntas Frecuentes'
  },
  'footer.privacy': {
    en: 'Privacy Policy',
    es: 'Política de Privacidad'
  },
  'footer.terms': {
    en: 'Terms of Service',
    es: 'Términos de Servicio'
  },
  'footer.cookies': {
    en: 'Cookie Policy',
    es: 'Política de Cookies'
  },
  'footer.copyright': {
    en: 'Qtap by STDI. All rights reserved.',
    es: 'Qtap by STDI. Todos los derechos reservados.'
  }
};
const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  toggleLang: () => {},
  t: (key: string) => key
});
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLang] = useState<Lang>('en');
  const toggleLang = useCallback(() => {
    setLang(prev => prev === 'en' ? 'es' : 'en');
  }, []);
  const t = useCallback((key: string) => {
    return translations[key]?.[lang] || key;
  }, [lang]);
  return <LanguageContext.Provider value={{
    lang,
    toggleLang,
    t
  }} data-id="element-211">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  return useContext(LanguageContext);
}